/**
 * StaffDashboard — "My Shift" view for frontline staff.
 *
 * Thin composition of existing generic components:
 *   - useListTasksQuery (filtered by assignedActorId)
 *   - useGetTaskStatisticsQuery
 *   - useListNotificationsForRecipientQuery
 *   - StatusBadge, SafetyBadge (generic)
 *   - PulseIndicator (backend-driven, no domain logic)
 *
 * Zero domain-specific field names. Tasks, actors, notifications
 * come from backend — frontend renders whatever is returned.
 *
 * DRR-055: Dumb Frontend.
 */

import { useMemo, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Paper,
  SimpleGrid,
  Card,
  Badge,
  Button,
  ThemeIcon,
  Loader,
  Alert,
  Tabs,
  ActionIcon,
  Tooltip,
  Progress,
} from '@mantine/core';
import {
  IconInbox,
  IconBell,
  IconCheck,
  IconClock,
  IconAlertTriangle,
  IconFlag,
  IconChevronRight,
  IconRefresh,
  IconUsers,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTenantId, useAuth } from '@/auth';
import { useMyActor } from '@/hooks/useMyActor';
import { useAssignedEntityIds } from '@ferp/workflow-forms';
import {
  useListTasksQuery,
  useListNotificationsForRecipientQuery,
  useGetTaskStatisticsQuery,
} from '@/generated/graphql';
import { normalizeMany } from '@/normalizers';
import { UnifiedStatus } from '@/types/normalized-entity';
import { SafetyBadge, StatusBadge } from '@/components/SafetyBadge';
import { PulseIndicator } from '@/components/PulseIndicator';

// ============================================================================
// COMPONENT
// ============================================================================

export function StaffDashboard() {
  const tenantId = useTenantId() ?? '';
  const { user } = useAuth();
  const skip = !tenantId;
  const navigate = useNavigate();

  // DRR-061: Resolve auth user to backend actor ID (canonical hook)
  const { actorId: resolvedActorId } = useMyActor();
  const actorId = resolvedActorId ?? '';

  // Backend queries — same hooks as UserDashboard, no domain-specific queries
  const { data: taskStatsData, loading: taskStatsLoading, refetch: refetchTaskStats } = useGetTaskStatisticsQuery({ skip });
  const { data: tasksData, loading: tasksLoading, refetch: refetchTasks } = useListTasksQuery({
    variables: { limit: 100 },
    skip,
  });
  const { data: notificationsData, loading: notificationsLoading, refetch: refetchNotifications } = useListNotificationsForRecipientQuery({
    variables: { recipientActorId: actorId, limit: 50 },
    skip: skip || !actorId,
  });

  // DRR-103 Phase 2: Scope-resolved assigned residents for "My Residents" tab
  const { assignedActorIds, isLoading: scopeLoading } = useAssignedEntityIds({
    actorId: actorId,
    skip: !actorId,
  });

  // Normalize for type-safe access
  const normalizedTasks = useMemo(
    () => normalizeMany(tasksData?.taskApiListTasks?.items ?? []),
    [tasksData],
  );
  const normalizedNotifications = useMemo(
    () => normalizeMany(notificationsData?.listNotificationsForRecipient?.items ?? []),
    [notificationsData],
  );

  // Filter to MY tasks (same pattern as TaskListPage "My Tasks" toggle)
  // Uses resolved Snowflake actorId, not auth ID
  const myTasks = useMemo(
    () => normalizedTasks.filter(t => t.domainData.assignedActor?.id === actorId),
    [normalizedTasks, actorId],
  );
  const myActiveTasks = useMemo(
    () => myTasks.filter(t => t.unifiedStatus !== UnifiedStatus.COMPLETED && t.unifiedStatus !== UnifiedStatus.CANCELLED),
    [myTasks],
  );
  const myCompletedTasks = useMemo(
    () => myTasks.filter(t => t.unifiedStatus === UnifiedStatus.COMPLETED),
    [myTasks],
  );

  // Stats from backend
  const stats = useMemo(() => {
    const taskStats = taskStatsData?.taskApiGetTaskStatistics;
    return {
      myActive: myActiveTasks.length,
      myCompleted: myCompletedTasks.length,
      slaBreached: taskStats?.slaBreachedCount ?? 0,
      pendingNotifications: normalizedNotifications.filter(n => n.unifiedStatus === UnifiedStatus.PENDING).length,
    };
  }, [taskStatsData, myActiveTasks, myCompletedTasks, normalizedNotifications]);

  const loading = taskStatsLoading || tasksLoading || notificationsLoading;

  const handleRefresh = useCallback(() => {
    refetchTaskStats();
    refetchTasks();
    refetchNotifications();
  }, [refetchTaskStats, refetchTasks, refetchNotifications]);

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to view the dashboard.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header with pulse indicator */}
        <Group justify="space-between">
          <Group gap="md">
            <div>
              <Title order={2}>My Shift</Title>
              <Text c="dimmed" size="sm">
                {user?.displayName ? `${user.displayName} — ` : ''}Tasks assigned to you
              </Text>
            </div>
            {/* Backend-computed PulseResult will be passed when API exists */}
            <PulseIndicator result={null} />
          </Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            onClick={handleRefresh}
            loading={loading}
          >
            Refresh
          </Button>
        </Group>

        {/* Overdue alert */}
        {stats.slaBreached > 0 && (
          <Alert color="red" title="Overdue Tasks" icon={<IconAlertTriangle />}>
            {stats.slaBreached} task{stats.slaBreached !== 1 ? 's are' : ' is'} past due.
          </Alert>
        )}

        {/* Stats — generic counts, no domain field names */}
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Card withBorder padding="md" style={{ cursor: 'pointer' }} onClick={() => navigate('/ferp-core/tasks')}>
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">My Tasks</Text>
              <ThemeIcon variant="light" size="sm" color="blue">
                <IconInbox size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{stats.myActive}</Text>
          </Card>

          <Card withBorder padding="md" style={{ cursor: 'pointer' }} onClick={() => navigate('/ferp-core/tasks')}>
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Completed</Text>
              <ThemeIcon variant="light" size="sm" color="green">
                <IconCheck size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{stats.myCompleted}</Text>
          </Card>

          <Card withBorder padding="md" style={{ cursor: 'pointer' }} onClick={() => navigate('/ferp-core/tasks')}>
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Overdue</Text>
              <ThemeIcon variant="light" size="sm" color={stats.slaBreached > 0 ? 'red' : 'gray'}>
                <IconClock size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700} c={stats.slaBreached > 0 ? 'red' : undefined}>{stats.slaBreached}</Text>
          </Card>

          <Card withBorder padding="md" style={{ cursor: 'pointer' }} onClick={() => navigate('/ferp-core/inbox')}>
            <Group justify="space-between" mb="xs">
              <Text size="sm" c="dimmed">Messages</Text>
              <ThemeIcon variant="light" size="sm" color="violet">
                <IconBell size={14} />
              </ThemeIcon>
            </Group>
            <Text size="xl" fw={700}>{stats.pendingNotifications}</Text>
          </Card>
        </SimpleGrid>

        {/* Task completion progress */}
        {myTasks.length > 0 && (
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={500}>Shift Progress</Text>
              <Text size="sm" c="dimmed">
                {stats.myCompleted}/{myTasks.length} ({myTasks.length > 0 ? Math.round((stats.myCompleted / myTasks.length) * 100) : 0}%)
              </Text>
            </Group>
            <Progress
              value={myTasks.length > 0 ? (stats.myCompleted / myTasks.length) * 100 : 0}
              color="green"
              size="sm"
              radius="xl"
            />
          </Paper>
        )}

        {/* Main content — tasks + notifications */}
        {loading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : (
          <Tabs defaultValue="tasks">
            <Tabs.List>
              <Tabs.Tab value="tasks" leftSection={<IconInbox size={14} />}>
                My Tasks ({myActiveTasks.length})
              </Tabs.Tab>
              <Tabs.Tab value="residents" leftSection={<IconUsers size={14} />}>
                My Residents ({assignedActorIds?.length ?? 0})
              </Tabs.Tab>
              <Tabs.Tab
                value="notifications"
                leftSection={<IconBell size={14} />}
                rightSection={
                  stats.pendingNotifications > 0 ? (
                    <Badge size="xs" variant="filled" color="red">
                      {stats.pendingNotifications}
                    </Badge>
                  ) : undefined
                }
              >
                Messages
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="tasks" pt="md">
              <Stack gap="xs">
                {myActiveTasks.length === 0 ? (
                  <Paper p="xl" withBorder ta="center">
                    <ThemeIcon size="xl" variant="light" color="green" mb="md">
                      <IconCheck size={24} />
                    </ThemeIcon>
                    <Text fw={500}>All caught up!</Text>
                    <Text size="sm" c="dimmed">No pending tasks assigned to you.</Text>
                  </Paper>
                ) : (
                  myActiveTasks.map((task) => (
                    <Paper
                      key={task.id}
                      p="sm"
                      withBorder
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ferp-core/tasks`)}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                          <ThemeIcon
                            color={task.unifiedStatus === UnifiedStatus.FAILED ? 'red' : task.unifiedStatus === UnifiedStatus.ACTIVE ? 'blue' : 'gray'}
                            variant="light"
                            size="sm"
                          >
                            <IconFlag size={12} />
                          </ThemeIcon>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={500} lineClamp={1}>{task.displayName}</Text>
                            <Group gap="xs" mt={2}>
                              <StatusBadge status={task.unifiedStatus} size="xs" />
                              <Badge size="xs" variant="outline">{task.domainData.priority}</Badge>
                              <SafetyBadge entity={task} size="xs" showDualApproval={false} />
                            </Group>
                          </div>
                        </Group>
                        <Tooltip label="View Task">
                          <ActionIcon variant="subtle" color="gray" onClick={(e) => { e.stopPropagation(); navigate(`/ferp-core/tasks`); }}>
                            <IconChevronRight size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Paper>
                  ))
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="residents" pt="md">
              <Stack gap="xs">
                {scopeLoading ? (
                  <Group justify="center" py="xl"><Loader /></Group>
                ) : !assignedActorIds || assignedActorIds.length === 0 ? (
                  <Paper p="xl" withBorder ta="center">
                    <ThemeIcon size="xl" variant="light" color="gray" mb="md">
                      <IconUsers size={24} />
                    </ThemeIcon>
                    <Text fw={500}>No assigned subjects</Text>
                    <Text size="sm" c="dimmed">
                      Subject assignments will appear here when configured by your supervisor.
                    </Text>
                  </Paper>
                ) : (
                  assignedActorIds.map((subjectId) => (
                    <Paper
                      key={subjectId}
                      p="sm"
                      withBorder
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/ferp-core/subjects/${subjectId}/checklist`)}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                          <ThemeIcon variant="light" size="sm" color="teal">
                            <IconUsers size={12} />
                          </ThemeIcon>
                          <Text size="sm" fw={500}>{subjectId}</Text>
                        </Group>
                        <Tooltip label="View Subject">
                          <ActionIcon variant="subtle" color="gray" onClick={(e) => { e.stopPropagation(); navigate(`/ferp-core/subjects/${subjectId}/checklist`); }}>
                            <IconChevronRight size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Paper>
                  ))
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="notifications" pt="md">
              <Stack gap="xs">
                {normalizedNotifications.length === 0 ? (
                  <Paper p="xl" withBorder ta="center">
                    <ThemeIcon size="xl" variant="light" color="gray" mb="md">
                      <IconBell size={24} />
                    </ThemeIcon>
                    <Text fw={500}>No messages</Text>
                    <Text size="sm" c="dimmed">Notifications will appear here.</Text>
                  </Paper>
                ) : (
                  normalizedNotifications.slice(0, 10).map((notif) => (
                    <Paper
                      key={notif.id}
                      p="sm"
                      withBorder
                      style={{
                        backgroundColor: notif.unifiedStatus === UnifiedStatus.PENDING ? 'var(--mantine-color-blue-light)' : undefined,
                      }}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                          <ThemeIcon variant="light" size="sm">
                            <IconBell size={12} />
                          </ThemeIcon>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" fw={notif.unifiedStatus === UnifiedStatus.PENDING ? 500 : 400} lineClamp={1}>
                              {notif.displayName}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={2}>{notif.domainData.messageTemplate}</Text>
                            <Text size="xs" c="dimmed" mt={2}>{new Date(notif.domainData.createdAt).toLocaleString()}</Text>
                          </div>
                        </Group>
                        <Group gap={4}>
                          <StatusBadge status={notif.unifiedStatus} size="xs" />
                          <Badge size="xs" color={notif.domainData.priority === 'HIGH' ? 'red' : 'gray'}>
                            {notif.domainData.priority}
                          </Badge>
                        </Group>
                      </Group>
                    </Paper>
                  ))
                )}
              </Stack>
            </Tabs.Panel>
          </Tabs>
        )}
      </Stack>
    </Container>
  );
}

export default StaffDashboard;
